
/* injects from baggage-loader */
require('./style.styl');

import React from 'react'
import PropTypes from 'prop-types'
import classnames from 'classnames'
import {
  SingleCalendar,
  RangeCalendar,
  WeekCalendar,
  YearCalendar,
  MonthCalendar
} from 'components/Calendar'
import formatDate from 'date-fns/format'
import Icon from 'components/Icon'
import partial from 'utils/partial'
import propsChanged from 'utils/propsChanged'
import moment from 'moment'

const format = date => formatDate(date, 'D MMM YY')
const calendarTypes = {
  'range': 'Range',
  'week': 'Week',
  'month': 'Month',
  'year': 'Year'
}
export default class DatePicker extends React.Component {
  static propTypes = {
    className: PropTypes.string,
    today: PropTypes.instanceOf(Date),
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    dynamicDateType: PropTypes.string,
    minDate: PropTypes.instanceOf(Date),
    maxDate: PropTypes.instanceOf(Date),
    modes: PropTypes.array,
    type: PropTypes.string,
    onSelect: PropTypes.func.isRequired,
    onChangeModes: PropTypes.func,
    disabled: PropTypes.bool,
    short: PropTypes.bool
  }

  static defaultProps = {
    minDate: null,
    modes: ['range', 'week', 'month', 'year']
  }

  constructor(props) {
    super(props)
    this.state = {
      selectedStartDate: null,
      selectedEndDate: null,
      isSelectInProgress: false,
      opened: false,
      type: props.type ?? props.modes[0]
    }
  }

  componentDidMount() {
    const {startDate, endDate, modes, type} = this.props
    const localTimeZone = moment.tz.guess()
    const defaultTimeZone = moment.defaultZone.name
    this.setState({
      selectedStartDate: startDate && this.convertTimeZone(startDate, defaultTimeZone, localTimeZone),
      selectedEndDate: endDate && this.convertTimeZone(endDate, defaultTimeZone, localTimeZone),
      type: type ?? modes[0]
    })
  }

  componentWillReceiveProps(nextProps) {
    if (propsChanged(this.props, nextProps, ['type', 'modes'])) {
      const {type, modes} = nextProps

      this.setState({
        type: type ?? modes[0]
      })
    }
  }

  componentWillUpdate(props, state) {
    if (state.opened) {
      document.addEventListener('click', this.hideCalendar)
    } else {
      document.removeEventListener('click', this.hideCalendar)
    }
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.hideCalendar)
  }

  render() {
    const {className, startDate, endDate, disabled} = this.props
    if (!startDate || !endDate) return null

    const calendarClassName = classnames('date-picker', className, {
      'is-disabled': disabled
    })

    return (
      <div className={calendarClassName}>
        {this.renderPicker()}
        {this.renderCalendarContainer()}
      </div>
    )
  }

  renderPicker() {
    return (
      <div className='date-picker_button' onClick={this.toggleCalendar}>
        <span className='date-picker_date'>{this.renderFormattedDate()}</span>
        <Icon name='calendar' className='date-picker_calendar-icon' width='20px' height='18px' />
      </div>
    )
  }

  renderFormattedDate() {
    const {type} = this.state
    const {startDate, endDate, dynamicDateType, short} = this.props
    const localTimeZone = moment.tz.guess()
    const defaultTimeZone = moment.defaultZone.name
    const lcStartDate = this.convertTimeZone(startDate, defaultTimeZone, localTimeZone)
    const lcEndDate = this.convertTimeZone(endDate, defaultTimeZone, localTimeZone)

    const isDatesEqual = lcStartDate.startOf('day').isSame(lcEndDate.startOf('day'))
    const standardFormat = `${lcStartDate.format('D MMM YY')} — ${lcEndDate.format('D MMM YY')}`
    switch (type) {
      case 'single':
        return lcStartDate.format('D MMM YY')
      case 'range':
        // if (dynamicDateType && rangePresets[dynamicDateType]) {
        //   return rangePresets[dynamicDateType]
        // } else {
        return isDatesEqual ? lcStartDate.format('D MMM YY') : standardFormat
        // }
      case 'week':
        return short
          ? `w/c ${lcStartDate.startOf('isoWeek').format('D MMM YY')}`
          : standardFormat
      case 'month':
        return short ? lcStartDate.format('MMM YY') : lcStartDate.format('MMMM YYYY')
      case 'year':
        return short ? lcStartDate.format('YYYY') : lcStartDate.format('YYYY')
      default:
        return standardFormat
    }
  }

  renderCalendarContainer() {
    const {opened, type} = this.state
    if (!opened) return null

    const classNames = classnames('date-picker_container', {
      [`is-${type}`]: true
    })

    return (
      <div className={classNames} onClick={this.calendarClickHandler}>
        {this.renderHeader()}
        <div className='date-picker_container_calendar'>
          {this.renderCalendar()}
        </div>
      </div>
    )
  }

  renderHeader() {
    const {modes} = this.props

    if (!modes || modes.length <= 1) return null

    return (
      <div className='date-picker_container_header'>
        {this.renderSelectedDates()}
        {this.renderTypes()}
      </div>
    )
  }

  renderSelectedDates() {
    const {type, selectedStartDate, selectedEndDate} = this.state
    const defaultTimeZone = moment.defaultZone.name
    const timeZone = moment.tz(defaultTimeZone).format("Z");;

    if (type !== 'range') return null

    return (
      <div className='date-picker_container_dates'>
        <div className='date-picker_container_date'>
          Start Date: {this.renderSelectedDate(selectedStartDate)}
        </div>
        <div className='date-picker_container_date'>
          End Date: {this.renderSelectedDate(selectedEndDate)}
        </div>
        <div className='date-picker_container_timezone'>
          UTC{timeZone}
        </div>
      </div>
    )
  }

  renderSelectedDate(date) {
    if (!date) {
      return (
        <span className='date-picker_container_date_empty'>
          Select date
        </span>
      )
    }

    return (
      <span className='date-picker_container_date_selected'>
        {format(date)}
      </span>
    )
  }

  renderCalendar() {
    const {minDate, maxDate, today, dynamicDateType} = this.props
    const {
      type,
      isSelectInProgress,
      selectedStartDate,
      selectedEndDate
    } = this.state

    const props = {
      minDate,
      maxDate: maxDate || today,
      dynamicDateType,
      today,
      selected: {start: selectedStartDate, end: selectedEndDate},
      isSelectInProgress,
      onSelect: this.selectRange,
      onApply: this.applyRange
    }

    switch (type) {
      case 'single':
        return <SingleCalendar {...props} />
      case 'range':
        return <RangeCalendar {...props} />
      case 'week':
        return <WeekCalendar {...props} />
      case 'month':
        return <MonthCalendar {...props} />
      case 'year':
        return <YearCalendar {...props} />
      default:
        return <RangeCalendar {...props} />
    }
  }

  renderTypes() {
    const {modes} = this.props
    const {type} = this.state

    return (
      <div className='date-picker_container_types'>
        {modes.map((key, index) => {
          const classNames = classnames('date-picker_container_type', {
            'is-active': type === key
          })
          return (
            <div
              key={index}
              className={classNames}
              onClick={partial(this.onTypeSelect, key)}
            >
              {calendarTypes[key]}
            </div>
          )
        })}
      </div>
    )
  }

  onTypeSelect = type => {
    const {onChangeModes} = this.props
    this.setState({type})
    onChangeModes(type)
  }

  convertTimeZone(time, fromTimezone, toTimezone) {
    const timeFormat = moment.tz(time, fromTimezone).format('YYYY-MM-DD HH:mm:ss')
    const mmStartDate = moment.tz(timeFormat, toTimezone)
    return mmStartDate
  }

  hideCalendar = () => {
    this.setState({
      opened: false
    })
  }

  calendarClickHandler = event => {
    return event.nativeEvent.stopImmediatePropagation()
  }

  toggleCalendar = () => {
    const {disabled} = this.props
    if (disabled) return null

    const {opened} = this.state

    this.setState({
      opened: !opened
    })
  }

  selectRange = (value, isSelectInProgress) => {
    const {onSelect} = this.props
    const {type} = this.state
    const {start, end} = value

    if (type === 'single') {
      const selectedValue = new Date(value)
      onSelect(moment([selectedValue.getFullYear(), selectedValue.getMonth(), selectedValue.getDate()]).toDate())
      this.hideCalendar()
    } else {
      this.setState({
        selectedStartDate: start,
        selectedEndDate: end,
        isSelectInProgress
      })
    }
  }

  applyRange = options => {
    const {onSelect} = this.props
    const {type} = this.state
    const {start, end, dynamicDateType} = options

    this.setState({
      selectedStartDate: start,
      selectedEndDate: end,
      isSelectInProgress: false,
      type,
      opened: false
    })

    const localTimeZone = moment.tz.guess()
    const defaultTimeZone = moment.defaultZone.name
    const mmStartDate = this.convertTimeZone(start, localTimeZone, defaultTimeZone)
    const mmEndDate = this.convertTimeZone(end, localTimeZone, defaultTimeZone)
    onSelect(mmStartDate, mmEndDate, dynamicDateType, type === 'range')
  }
}



// WEBPACK FOOTER //
// ./client/components/DatePicker/index.js